import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import DashboardLayout from './components/DashboardLayout';
import StatsCard from './components/StatsCard';
import QRCodeList from './components/QRCodeList';
import ShortLinkList from './components/ShortLinkList';
import BioLinkList from './components/BioLinkList';
import UpgradeModal from './components/UpgradeModal';
import { useSubscription } from '../../hooks/useSubscription';
import { useToast } from '../../hooks/useToast';
import { qrCodeService, shortLinkService, bioLinkService } from '../../utils/storage';

export default function DashboardPage() {
  const { t } = useTranslation('dashboard');
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    totalScans: 0,
    totalClicks: 0,
    totalViews: 0,
  });
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const { showToast } = useToast();
  const { subscription, usage, loading: subLoading, refetch } = useSubscription();
  const [activeTab, setActiveTab] = useState<'bio' | 'qr' | 'links'>('bio');
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  useEffect(() => {
    checkAuth();
  }, []);

  useEffect(() => {
    if (authChecked) {
      loadStats();
    }
  }, [authChecked]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'true') {
      setShowUpgradeModal(true);
      window.history.replaceState({}, '', '/dashboard');
    }
    if (params.get('success') === 'true') {
      showToast('구독이 성공적으로 완료되었습니다! 🎉', 'success');
      refetch();
      window.history.replaceState({}, '', '/dashboard');
    }
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        navigate('/login');
        return;
      }
      setAuthChecked(true);
    } catch (error) {
      console.error('인증 확인 실패:', error);
      navigate('/login');
    }
  };

  const loadStats = async () => {
    try {
      setLoading(true);
      const [qrStats, linkStats, bioStats] = await Promise.all([
        qrCodeService.getStats(),
        shortLinkService.getStats(),
        bioLinkService.getStats(),
      ]);

      setStats({
        totalScans: qrStats.totalScans,
        totalClicks: linkStats.totalClicks,
        totalViews: bioStats.totalViews,
      });
    } catch (error) {
      console.error('통계 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!authChecked) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <i className="ri-loader-4-line text-4xl text-teal-500 animate-spin"></i>
          <p className="text-gray-600 mt-4">로딩 중...</p>
        </div>
      </div>
    );
  }

  const isPro = subscription?.plan_type === 'pro';

  return (
    <DashboardLayout>
      <div className="space-y-8">
        {/* Stats Overview */}
        <div>
          <h2 className="text-2xl font-bold text-gray-900 mb-6">{t('dashboard.overview')}</h2>
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[1, 2, 3].map((i) => (
                <div key={i} className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 animate-pulse">
                  <div className="h-4 bg-gray-200 rounded w-1/2 mb-4"></div>
                  <div className="h-8 bg-gray-200 rounded w-3/4"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <StatsCard
                title={t('stats.totalViews')}
                value={stats.totalViews.toLocaleString()}
                icon="ri-eye-line"
                trend=""
                trendUp={true}
              />
              <StatsCard
                title={t('stats.totalScans')}
                value={stats.totalScans.toLocaleString()}
                icon="ri-qr-scan-2-line"
                trend=""
                trendUp={true}
              />
              <StatsCard
                title={t('stats.totalClicks')}
                value={stats.totalClicks.toLocaleString()}
                icon="ri-cursor-line"
                trend=""
                trendUp={true}
              />
            </div>
          )}
        </div>

        {/* Bio Links Section - Now First */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.biolinks')}</h2>
          </div>
          <BioLinkList />
        </div>

        {/* QR Codes Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.qrcodes')}</h2>
          </div>
          <QRCodeList />
        </div>

        {/* Short Links Section */}
        <div>
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900">{t('dashboard.links')}</h2>
          </div>
          <ShortLinkList />
        </div>
      </div>

      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />
    </DashboardLayout>
  );
}
